import { memo, useMemo } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import RemoveIcon from '@mui/icons-material/Remove'
import { Button } from 'Common/Supplyvue-ui'
import { useSchedulerData } from 'Features/SchedulerView/SchedulerView.data'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
import { computeScheduleStats } from './SchedulerRunSummary.view'
import { useConsolidatedWeeks } from './Scheduler.data'
import { weekBaseHourFor } from 'Features/SchedulerView/ClipToWeek'
import { MiniGantt } from './ScheduleGantt.view'

// ─── Types ──────────────────────────────────────────────────────────

type ComparisonProps = {
  open: boolean
  onClose: () => void
  scheduleId: string
  iterationA: string
  iterationB: string
  selectedWeek: number
  versionNameA?: string
  versionNameB?: string
}

type MetricDiff = {
  label: string
  valueA: string | number
  valueB: string | number
  delta: number
  unit: string
  lowerIsBetter: boolean
}

// ─── Delta indicator ────────────────────────────────────────────────

const DeltaChip = memo(
  ({
    delta,
    unit,
    lowerIsBetter,
  }: {
    delta: number
    unit: string
    lowerIsBetter: boolean
  }) => {
    if (Math.abs(delta) < 0.01) {
      return (
        <Chip
          icon={<RemoveIcon sx={{ fontSize: '12px !important' }} />}
          label="No change"
          size="small"
          sx={{ fontSize: '0.6rem', height: 20, color: 'text.secondary' }}
          variant="outlined"
        />
      )
    }

    const isImproved = lowerIsBetter ? delta < 0 : delta > 0
    const color = isImproved ? '#2e7d32' : '#d32f2f'
    const Icon = delta > 0 ? ArrowUpwardIcon : ArrowDownwardIcon
    const sign = delta > 0 ? '+' : ''

    return (
      <Chip
        icon={
          <Icon
            sx={{ fontSize: '12px !important', color: `${color} !important` }}
          />
        }
        label={`${sign}${typeof delta === 'number' && !Number.isInteger(delta) ? delta.toFixed(1) : delta}${unit}`}
        size="small"
        sx={{
          fontSize: '0.65rem',
          height: 22,
          fontWeight: 700,
          color,
          bgcolor: `${color}10`,
          borderColor: `${color}30`,
          '& .MuiChip-icon': { color },
        }}
        variant="outlined"
      />
    )
  }
)
DeltaChip.displayName = 'DeltaChip'

// ─── Metric Row ─────────────────────────────────────────────────────

const MetricRow = memo(({ metric }: { metric: MetricDiff }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: 0.75,
      px: 1.5,
      '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
      borderRadius: 1,
    }}
  >
    <Typography
      sx={{
        fontSize: '0.7rem',
        fontWeight: 500,
        flex: '0 0 140px',
        color: 'text.secondary',
      }}
    >
      {metric.label}
    </Typography>
    <Typography
      sx={{
        fontSize: '0.75rem',
        fontWeight: 600,
        flex: '0 0 90px',
        textAlign: 'right',
        fontFamily: 'monospace',
      }}
    >
      {metric.valueA}
      {metric.unit}
    </Typography>
    <Typography
      sx={{
        fontSize: '0.75rem',
        fontWeight: 600,
        flex: '0 0 90px',
        textAlign: 'right',
        fontFamily: 'monospace',
      }}
    >
      {metric.valueB}
      {metric.unit}
    </Typography>
    <Box
      sx={{ flex: '0 0 120px', display: 'flex', justifyContent: 'flex-end' }}
    >
      <DeltaChip
        delta={metric.delta}
        unit={metric.unit}
        lowerIsBetter={metric.lowerIsBetter}
      />
    </Box>
  </Box>
))
MetricRow.displayName = 'MetricRow'

// ─── Main Component ─────────────────────────────────────────────────

export const SchedulerComparison = memo(
  ({
    open,
    onClose,
    scheduleId,
    iterationA,
    iterationB,
    selectedWeek,
    versionNameA,
    versionNameB,
  }: ComparisonProps) => {
    const { data: dataA, isLoading: loadingA } = useSchedulerData(
      scheduleId,
      iterationA,
      selectedWeek
    )
    const { data: dataB, isLoading: loadingB } = useSchedulerData(
      scheduleId,
      iterationB,
      selectedWeek
    )

    const rowsA: InputRow[] = dataA?.data ?? []
    const rowsB: InputRow[] = dataB?.data ?? []

    // Each iteration carries its own `consolidated_weeks`, so the offset has
    // to be derived per side — one of the two versions being compared may
    // have been run consolidated and the other not, and then a single shared
    // offset would misreport exactly the number this dialog exists to diff.
    const consolidatedA = useConsolidatedWeeks(scheduleId, iterationA)
    const consolidatedB = useConsolidatedWeeks(scheduleId, iterationB)

    const statsA = useMemo(
      () =>
        computeScheduleStats(
          rowsA,
          weekBaseHourFor(consolidatedA, selectedWeek)
        ),
      [rowsA, consolidatedA, selectedWeek]
    )
    const statsB = useMemo(
      () =>
        computeScheduleStats(
          rowsB,
          weekBaseHourFor(consolidatedB, selectedWeek)
        ),
      [rowsB, consolidatedB, selectedWeek]
    )

    const metrics = useMemo((): MetricDiff[] => {
      if (!statsA || !statsB) return []
      return [
        {
          label: 'Makespan',
          valueA: statsA.makespan,
          valueB: statsB.makespan,
          delta: statsB.makespan - statsA.makespan,
          unit: 'h',
          lowerIsBetter: true,
        },
        {
          label: 'Production Hours',
          valueA: statsA.totalLineHours.toFixed(1),
          valueB: statsB.totalLineHours.toFixed(1),
          delta: statsB.totalLineHours - statsA.totalLineHours,
          unit: 'h',
          lowerIsBetter: false,
        },
        {
          label: 'Total Quantity',
          valueA: statsA.totalQuantity.toLocaleString(),
          valueB: statsB.totalQuantity.toLocaleString(),
          delta: statsB.totalQuantity - statsA.totalQuantity,
          unit: '',
          lowerIsBetter: false,
        },
        {
          label: 'Products',
          valueA: statsA.productCount,
          valueB: statsB.productCount,
          delta: statsB.productCount - statsA.productCount,
          unit: '',
          lowerIsBetter: false,
        },
        {
          label: 'Lines',
          valueA: statsA.lineCount,
          valueB: statsB.lineCount,
          delta: statsB.lineCount - statsA.lineCount,
          unit: '',
          lowerIsBetter: false,
        },
        {
          label: 'Changeovers',
          valueA: statsA.changeovers,
          valueB: statsB.changeovers,
          delta: statsB.changeovers - statsA.changeovers,
          unit: '',
          lowerIsBetter: true,
        },
        {
          label: 'Avg Line Util',
          valueA: statsA.avgLineUtil,
          valueB: statsB.avgLineUtil,
          delta: statsB.avgLineUtil - statsA.avgLineUtil,
          unit: '%',
          lowerIsBetter: false,
        },
        {
          label: 'Schedule Rows',
          valueA: statsA.rowCount,
          valueB: statsB.rowCount,
          delta: statsB.rowCount - statsA.rowCount,
          unit: '',
          lowerIsBetter: false,
        },
      ]
    }, [statsA, statsB])

    const isLoading = loadingA || loadingB

    // Group rows by line for mini Gantt comparison
    const linesA = useMemo(() => {
      const grouped: Record<string, InputRow[]> = {}
      rowsA.forEach((r) => {
        if (!grouped[r.line]) grouped[r.line] = []
        grouped[r.line].push(r)
      })
      return grouped
    }, [rowsA])

    const linesB = useMemo(() => {
      const grouped: Record<string, InputRow[]> = {}
      rowsB.forEach((r) => {
        if (!grouped[r.line]) grouped[r.line] = []
        grouped[r.line].push(r)
      })
      return grouped
    }, [rowsB])

    const allLines = useMemo(() => {
      return [
        ...new Set([...Object.keys(linesA), ...Object.keys(linesB)]),
      ].sort()
    }, [linesA, linesB])

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            maxHeight: '85vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pb: 1,
            borderBottom: '2px solid',
            borderColor: 'secondary.main',
          }}
        >
          <CompareArrowsIcon color="secondary" />
          Scenario Comparison — Week {selectedWeek}
        </DialogTitle>

        <DialogContent sx={{ pt: '20px !important' }}>
          {isLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={32} />
              <Typography
                sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1 }}
              >
                Loading versions...
              </Typography>
            </Box>
          ) : (
            <>
              {/* Metrics comparison table */}
              <Box sx={{ mb: 3 }}>
                {/* Column headers */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    pb: 0.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'text.disabled',
                      flex: '0 0 140px',
                    }}
                  >
                    Metric
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'primary.main',
                      flex: '0 0 90px',
                      textAlign: 'right',
                    }}
                  >
                    {versionNameA || `V${iterationA}`}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'secondary.main',
                      flex: '0 0 90px',
                      textAlign: 'right',
                    }}
                  >
                    {versionNameB || `V${iterationB}`}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'text.disabled',
                      flex: '0 0 120px',
                      textAlign: 'right',
                    }}
                  >
                    Delta
                  </Typography>
                </Box>
                {metrics.map((metric) => (
                  <MetricRow key={metric.label} metric={metric} />
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Side-by-side Gantt comparison */}
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                  mb: 1.5,
                }}
              >
                Line Timeline Comparison
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {allLines.map((line) => (
                  <Box
                    key={line}
                    sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        minWidth: 70,
                        flex: '0 0 70px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {line}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <MiniGantt rows={linesA[line] ?? []} mode="line" />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <MiniGantt rows={linesB[line] ?? []} mode="line" />
                    </Box>
                  </Box>
                ))}
                {allLines.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      mt: 0.5,
                    }}
                  >
                    <Box sx={{ minWidth: 70, flex: '0 0 70px' }} />
                    <Typography
                      sx={{
                        flex: 1,
                        fontSize: '0.6rem',
                        color: 'primary.main',
                        textAlign: 'center',
                        fontWeight: 600,
                      }}
                    >
                      {versionNameA || `Version ${iterationA}`}
                    </Typography>
                    <Typography
                      sx={{
                        flex: 1,
                        fontSize: '0.6rem',
                        color: 'secondary.main',
                        textAlign: 'center',
                        fontWeight: 600,
                      }}
                    >
                      {versionNameB || `Version ${iterationB}`}
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
)

SchedulerComparison.displayName = 'SchedulerComparison'
